import { find, flatMap, head, isEmpty, isNil, map, some, sortBy } from "lodash";
import { useRouter } from "next/router";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
    Button,
    BUTTON_SEVERITIES,
    Dropdown,
    MESSAGE_TYPES,
    Modal,
} from "../../../../common/components";
import { API_REST_METHODS } from "../../../../common/constants";
import { fetcher } from "../../../../common/hooks";
import { showToast } from "../../../../common/services/toast";
import { isTimeValid } from "../../../../common/utils";
import { INTERVALS } from "../../constants";
import { Notification, NotificationsConfig } from "../../models";
import { useNotificationsStore } from "../../store";
import { NotificationsConfigCard } from "../NotificationsConfigCard";

const requestPatchNotificationsConfig = (
    url: string,
    { arg }: { arg: NotificationsConfig }
) =>
    fetcher(url, {
        options: {
            method: API_REST_METHODS.PATCH,
            body: JSON.stringify(arg),
        },
    });

const EmailNotificationModal = () => {
    const { closeEmailNotificationModal, isOpen, userId } =
        useNotificationsStore();

    const router = useRouter();
    const { companyId } = router.query as {
        companyId?: string;
    };
    const { t } = useTranslation(["common", "toast"]);

    const [editedNotificationsConfig, setEditedNotificationsConfig] =
            useState<NotificationsConfig>(),
        [selectedCompanyId, setSelectedCompanyId] = useState<string>();

    const {
        data: notificationsConfig,
        isLoading: isNotificationsConfigLoading,
    } = useSWR(
        isOpen
            ? `/api/notification-management/v1/users/${
                  userId || "me"
              }/notifications`
            : null
    );

    const {
        trigger: triggerSaveChanges,
        isMutating: isMutatingNotificationsUpdate,
    } = useSWRMutation(
        `/api/notification-management/v1/users/${userId || "me"}/notifications`,
        requestPatchNotificationsConfig,
        {
            onSuccess: () => {
                showToast({
                    severity: MESSAGE_TYPES.SUCCESS,
                    summary: t("toast:emailNotifications.updated.title"),
                    detail: t("toast:emailNotifications.updated.description"),
                });
                closeEmailNotificationModal();
            },
        }
    );

    const filteredCompanies = useMemo(
        () =>
            sortBy(editedNotificationsConfig?.companies, "name").filter(
                (company) => company.id === selectedCompanyId
            ),
        [editedNotificationsConfig?.companies, selectedCompanyId]
    );
    const filteredLocations = useMemo(
        () => sortBy(flatMap(filteredCompanies, "locations"), "name"),
        [filteredCompanies]
    );

    const companiesOptions = map(
        notificationsConfig?.companies,
        ({ id, name }) => ({
            id,
            name,
        })
    );

    const checkInvalidNotifications = (notifications?: Notification[]) =>
        some(notifications, (notification?: Notification) => {
            if (notification?.active && !notification?.interval) {
                return true;
            }
            if (
                notification?.interval &&
                notification.interval !== INTERVALS.IMMEDIATELY &&
                !isTimeValid(notification?.time)
            ) {
                return true;
            }
            return false;
        });

    const isOkButtonDisabled = useMemo(() => {
        if (isMutatingNotificationsUpdate) {
            return true;
        }

        if (
            checkInvalidNotifications(
                flatMap(filteredCompanies, "notifications")
            ) ||
            checkInvalidNotifications(
                flatMap(filteredLocations, "notifications")
            )
        ) {
            return true;
        }

        return false;
    }, [filteredCompanies, filteredLocations, isMutatingNotificationsUpdate]);
    const showCompanyDropdown = companiesOptions?.length >= 2 && !userId;

    useEffect(() => {
        if (notificationsConfig) {
            // set first company as default value when opened from user overview (userId defined)
            // set company from URL when opened from avatar (userId not defined)
            const newSelectedCompanyId = userId
                ? companyId
                : head(sortBy(notificationsConfig?.companies, "name"))?.id;
            setSelectedCompanyId(newSelectedCompanyId);
        }
    }, [notificationsConfig, userId, companyId]);

    useEffect(() => {
        // reset notificationConfig when selectedCompanyId changes
        setEditedNotificationsConfig(notificationsConfig);
    }, [selectedCompanyId, notificationsConfig]);

    const handleDisableAllNotificatins = () =>
        setEditedNotificationsConfig((prevValue) => {
            if (!prevValue) {
                return;
            }
            const getDisabledNotifications = (notifications: Notification[]) =>
                notifications?.map((notification) => ({
                    ...notification,
                    active: false,
                    interval: undefined,
                }));

            return {
                ...prevValue,
                companies: prevValue.companies.map((company) => ({
                    ...company,
                    notifications: !isNil(company.notifications)
                        ? getDisabledNotifications(company.notifications)
                        : undefined,
                    locations: company.locations.map((location) => ({
                        ...location,
                        notifications: getDisabledNotifications(
                            location.notifications
                        ),
                    })),
                })),
            };
        });

    const handleOnChangeCompanyNotification = (
        id: string,
        newNotification: Notification
    ) => {
        const changedCompany = find(filteredCompanies, { id });

        if (!changedCompany) {
            return;
        }

        setEditedNotificationsConfig((prevValue) => {
            if (!prevValue) {
                return;
            }
            const newCompanyData = {
                ...changedCompany,
                notifications: changedCompany.notifications?.map(
                    (notification) =>
                        notification.type === newNotification.type
                            ? newNotification
                            : notification
                ),
            };
            return {
                ...prevValue,
                companies: prevValue.companies.map((company) =>
                    company.id === id ? newCompanyData : company
                ),
            };
        });
    };

    const handleOnChangeLocationNotification = (
        id: string,
        newNotification: Notification
    ) => {
        const changedCompany = find(filteredCompanies, { locations: [{ id }] });

        if (!changedCompany) {
            return;
        }

        setEditedNotificationsConfig((prevValue) => {
            if (!prevValue) {
                return;
            }
            const newLocationsData = changedCompany.locations.map((location) =>
                location.id === id
                    ? {
                          ...location,
                          notifications: location.notifications.map(
                              (notification) =>
                                  notification.type === newNotification.type
                                      ? newNotification
                                      : notification
                          ),
                      }
                    : location
            );
            const newCompanyData = {
                ...changedCompany,
                locations: newLocationsData,
            };
            return {
                ...prevValue,
                companies: prevValue.companies.map((company) =>
                    company.id === changedCompany.id ? newCompanyData : company
                ),
            };
        });
    };

    const handleOnChangeSendNotification = (isSendConfirmation: boolean) => {
        setEditedNotificationsConfig((prevValue) => {
            if (!prevValue) {
                return;
            }

            return {
                ...prevValue,
                sendConfirmation: isSendConfirmation,
            };
        });
    };

    const handleSaveChanges = () => {
        if (!editedNotificationsConfig || isOkButtonDisabled) {
            return;
        }
        triggerSaveChanges(editedNotificationsConfig);
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                title={t("emailNotifications.self")}
                onClickClose={closeEmailNotificationModal}
                onClickCancel={closeEmailNotificationModal}
                hasCancelButton
                hasOkButton
                isOkButtonDisabled={isOkButtonDisabled}
                onClickOk={handleSaveChanges}
            >
                {isNotificationsConfigLoading ? (
                    <ProgressSpinner className="[&>svg>circle]:stroke-primary-500! w-14 flex justify-center" />
                ) : (
                    <div className="flex flex-col gap-3.5">
                        <div className="flex flex-row items-center justify-between pb-3.5 pt-1">
                            {t("emailNotifications.description")}
                            <Button
                                label={t("emailNotifications.disableAll") || ""}
                                severity={BUTTON_SEVERITIES.INFO}
                                text
                                onClick={handleDisableAllNotificatins}
                                className="p-1.5"
                            />
                        </div>
                        {!showCompanyDropdown && (
                            <div className="flex flex-col">
                                <label className="text-text-500/60">
                                    {t("common:selectCompany")}
                                </label>
                                <div>
                                    <Dropdown
                                        className="min-w-52"
                                        value={selectedCompanyId}
                                        onChange={(e) =>
                                            setSelectedCompanyId(e.value)
                                        }
                                        options={companiesOptions}
                                        optionLabel="name"
                                        optionValue="id"
                                    />
                                </div>
                            </div>
                        )}
                        {filteredCompanies
                            .filter(
                                (company) => !isEmpty(company?.notifications)
                            )
                            .map((company) => (
                                <NotificationsConfigCard
                                    key={company.id}
                                    title={company.name}
                                    description={t(
                                        "emailNotifications.companyDescription"
                                    )}
                                    notifications={company.notifications!}
                                    onChange={(notification) =>
                                        handleOnChangeCompanyNotification(
                                            company.id,
                                            notification
                                        )
                                    }
                                />
                            ))}
                        {filteredLocations
                            .filter(
                                (location) => !isEmpty(location?.notifications)
                            )
                            .map((location) => (
                                <NotificationsConfigCard
                                    key={location.id}
                                    title={location.name}
                                    description={t(
                                        "emailNotifications.locationDescription",
                                        { locationName: location.name }
                                    )}
                                    notifications={location.notifications!}
                                    onChange={(notification) =>
                                        handleOnChangeLocationNotification(
                                            location.id,
                                            notification
                                        )
                                    }
                                />
                            ))}
                        <div className="flex items-center justify-center gap-1.5">
                            <Checkbox
                                inputId="sendNotification"
                                onChange={(e) =>
                                    handleOnChangeSendNotification(!!e.checked)
                                }
                                checked={
                                    !!editedNotificationsConfig?.sendConfirmation
                                }
                            />
                            <label htmlFor="sendNotification">
                                {t(
                                    "common:emailNotifications.sendConfirmation"
                                )}
                            </label>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default EmailNotificationModal;
